import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Center,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  toast,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CREATE_ADDRESSES,
  UPDATE_ADDRESS,
} from "../../services/graphql/mutation/Addresses";
import { LOCATIONS } from "../../services/graphql/query/Location";
import { ILocation } from "../../services/graphql/Location";
import { useUnauthorizedAction } from "../../services/hooks/useUnauthorizedAction";
import { MessageError } from "../../shared/MessageError";
import { MessageSuccess } from "../../shared/MessageSuccess";
import { IAddress } from "../../services/graphql/Address";
import { ContextApp } from "../../shared/ContextApp";

interface IFormCreateAddress {
  editMode?: boolean;
  addressToUpdate?: IAddress;
  onSuccess?: () => void;
  onError?: () => void;
}
export const FormCreateAddress = (props: IFormCreateAddress) => {
  const { onSuccess, onError, editMode, addressToUpdate } = props;
  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(true);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [createAddress] = useMutation(CREATE_ADDRESSES);
  const [updateAddress] = useMutation(UPDATE_ADDRESS);
  const { action } = useUnauthorizedAction();
  const toast = useToast();
  const unauthorized = useCallback(() => {
    action();
  }, []);
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm();
  useQuery(LOCATIONS, {
    onCompleted: (data) => {
      const { locations } = data;
      setLocations(locations);
      setLoadingLocations(false);
    },
    onError: (error) => {
      setLoadingLocations(false);
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError(
            error.message ? error.message : "Error al recuperar las ciudades."
          ),
      });
    },
  });
  function onSubmitCreate(values: any) {
    if (values.street_number) {
      values.street_number = Number(values.street_number);
    } else {
      delete values.street_number;
    }
    if (values.floor) {
      values.floor = Number(values.floor);
    } else {
      delete values.floor;
    }
    if (values.location_id) {
      values.location_id = Number(values.location_id);
    } else {
      delete values.location_id;
    }
    setLoadingForm(true);
    createAddress({
      variables: {
        input: { ...values },
      },
    })
      .then(() => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "Tu dirección se registró exitosamente!.",
            }),
        });
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Ocurrió un error al registrar su dirección. Por favor intente nuevamente."
              ),
          });
          if (onError) {
            onError();
          }
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoadingForm(false);
      });
  }

  function onSubmitUpdate(values: any) {
    if (!addressToUpdate?.id) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError(
            "No pudimos encontrar la dirección para poder acutalizarla."
          ),
      });
      if (onError) {
        onError();
      }
      return;
    }
    if (values.street_number) {
      values.street_number = Number(values.street_number);
    } else {
      delete values.street_number;
    }
    if (values.floor) {
      values.floor = Number(values.floor);
    } else {
      delete values.floor;
    }
    if (values.location_id) {
      values.location_id = Number(values.location_id);
    } else {
      delete values.location_id;
    }
    setLoadingForm(true);
    updateAddress({
      variables: {
        input: { id: addressToUpdate.id, ...values },
      },
    })
      .then(() => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "Tu dirección se actualizó exitosamente!.",
            }),
        });
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Ocurrió un error al actualizar su dirección. Por favor intente nuevamente."
              ),
          });
          if (onError) {
            onError();
          }
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoadingForm(false);
      });
  }

  return (
    <>
      {editMode && loadingLocations ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          <Flex
            alignItems={{ base: "flex-start", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            mb={2}
          >
            <CloseButton
              ml={"auto"}
              display={{ base: "block", sm: "none" }}
              onClick={() => {
                if (onSuccess) {
                  onSuccess();
                }
              }}
            />
            <Heading mb={0} as={"h4"}>
              {!editMode ? "Agregar dirección" : "Actualizar dirección"}
            </Heading>
            <CloseButton
              ml={"auto"}
              display={{ base: "none", sm: "block" }}
              onClick={() => {
                if (onSuccess) {
                  onSuccess();
                }
              }}
            />
          </Flex>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(!editMode ? onSubmitCreate : onSubmitUpdate)}
          >
            <FormControl isInvalid={errors.location_id}>
              <FormLabel>Localidad</FormLabel>
              <Select
                defaultValue={addressToUpdate?.location_id.toString()}
                variant={"filled"}
                placeholder="Seleccione localidad"
                id="location_id"
                {...register("location_id", {
                  required: "Debe seleccionar una localidad",
                })}
              >
                {locations.map((location) => (
                  <option value={location.id}>{location.name}</option>
                ))}
              </Select>
              <FormErrorMessage>
                {errors.location_id && errors.location_id.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.street_name}>
              <FormLabel>Calle</FormLabel>
              <Input
                defaultValue={addressToUpdate?.street_name}
                maxLength={30}
                placeholder="Ingrese calle..."
                id="street_name"
                {...register("street_name", {
                  required: "Debe ingresar una calle",
                })}
              />
              <FormErrorMessage>
                {errors.street_name && errors.street_name.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.street_number}>
              <FormLabel>Altura</FormLabel>
              <NumberInput defaultValue={addressToUpdate?.street_number}>
                <NumberInputField
                  placeholder="Ingrese altura..."
                  id="street_number"
                  {...register("street_number", {
                    required: "Debe especificar la altura",
                  })}
                />
              </NumberInput>
              <FormErrorMessage>
                {errors.street_number && errors.street_number.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Piso</FormLabel>
              <NumberInput defaultValue={addressToUpdate?.floor}>
                <NumberInputField
                  id="floor"
                  {...register("floor", {})}
                  placeholder="Ingrese piso..."
                />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel>Departamento</FormLabel>
              <Input
                defaultValue={addressToUpdate?.department}
                id="department"
                {...register("department", {})}
                maxLength={2}
                placeholder="Ingrese departamento..."
              />
            </FormControl>
            <Button
              isLoading={loadingForm || loadingLocations}
              type="submit"
              w={"100%"}
              mt={4}
            >
              {!editMode ? "Agregar" : "Guardar"}
            </Button>
          </form>
        </>
      )}
    </>
  );
};
